import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  saveIntegration,
  testIntegration,
  testEmailSmtp,
  type IntegrationProvider,
  type TestIntegrationResponse,
} from '../../api/integrations'

type EmailImapFormProps = {
  stationId: number
  provider?: IntegrationProvider
  initializing?: boolean
  onSaved: () => void | Promise<void>
  onNotify: (severity: 'success' | 'error', message: string) => void
}

type EmailFormState = {
  imapHost: string
  imapPort: string
  imapEncryption: 'ssl' | 'tls' | 'none'
  username: string
  password: string
  hasStoredPassword: boolean
  mailbox: string
  pollInterval: string
  smtpHost: string
  smtpPort: string
  smtpEncryption: 'ssl' | 'tls' | 'none'
  smtpUsername: string
  smtpPassword: string
  hasStoredSmtpPassword: boolean
  fromName: string
  fromEmail: string
  replyToEmail: string
  returnPath: string
}

type FormErrors = Partial<Record<keyof EmailFormState, string>>

const IMAP_ERROR_FIELDS: Array<keyof EmailFormState> = [
  'imapHost',
  'imapPort',
  'imapEncryption',
  'username',
  'password',
  'pollInterval',
]

const SMTP_ERROR_FIELDS: Array<keyof EmailFormState> = [
  'smtpHost',
  'smtpPort',
  'smtpEncryption',
  'smtpUsername',
  'smtpPassword',
  'fromEmail',
  'replyToEmail',
  'returnPath',
]

const encryptionOptions: Array<{ value: 'ssl' | 'tls' | 'none'; label: string }> = [
  { value: 'ssl', label: 'SSL' },
  { value: 'tls', label: 'TLS' },
  { value: 'none', label: 'None' },
]

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const buildStateFromProvider = (provider?: IntegrationProvider): EmailFormState => {
  const status = provider?.status ?? 'not_configured'
  const settings: Record<string, unknown> = status === 'corrupt' ? {} : provider?.settings ?? {}
  const imapHost = typeof settings.imap_host === 'string' ? settings.imap_host : ''
  const imapPortRaw = settings.imap_port
  const imapPort =
    typeof imapPortRaw === 'number'
      ? String(imapPortRaw)
      : typeof imapPortRaw === 'string' && imapPortRaw !== ''
        ? imapPortRaw
        : '993'
  const rawEncryption =
    typeof settings.imap_encryption === 'string'
      ? settings.imap_encryption.toLowerCase()
      : 'ssl'
  const imapEncryption = (['ssl', 'tls', 'none'] as const).includes(rawEncryption as 'ssl' | 'tls' | 'none')
    ? (rawEncryption as 'ssl' | 'tls' | 'none')
    : 'ssl'
  const username = typeof settings.username === 'string' ? settings.username : ''
  const mailbox =
    typeof settings.mailbox === 'string' && settings.mailbox !== ''
      ? settings.mailbox
      : 'INBOX'
  const pollIntervalRaw = settings.poll_interval_minutes
  const pollInterval = typeof pollIntervalRaw === 'number'
    ? String(Math.min(30, Math.max(1, pollIntervalRaw)))
    : typeof pollIntervalRaw === 'string' && pollIntervalRaw !== ''
      ? pollIntervalRaw
      : '2'
  const hasStoredPassword =
    status === 'ok' && typeof settings.password === 'string' && settings.password === '__REDACTED__'
  const smtpHost = typeof settings.smtp_host === 'string' ? settings.smtp_host : ''
  const smtpPortRaw = settings.smtp_port
  const smtpPort = typeof smtpPortRaw === 'number'
    ? String(smtpPortRaw)
    : typeof smtpPortRaw === 'string' && smtpPortRaw !== ''
      ? smtpPortRaw
      : '587'
  const rawSmtpEncryption =
    typeof settings.smtp_encryption === 'string'
      ? settings.smtp_encryption.toLowerCase()
      : 'tls'
  const smtpEncryption = (['ssl', 'tls', 'none'] as const).includes(rawSmtpEncryption as 'ssl' | 'tls' | 'none')
    ? (rawSmtpEncryption as 'ssl' | 'tls' | 'none')
    : 'tls'
  const smtpUsername = typeof settings.smtp_username === 'string' ? settings.smtp_username : ''
  const fromName = typeof settings.from_name === 'string' ? settings.from_name : ''
  const fromEmail = typeof settings.from_email === 'string' ? settings.from_email : ''
  const replyToEmail = typeof settings.reply_to_email === 'string' ? settings.reply_to_email : ''
  const returnPath = typeof settings.return_path === 'string' ? settings.return_path : ''
  const hasStoredSmtpPassword =
    status === 'ok' && typeof settings.smtp_password === 'string' && settings.smtp_password === '__REDACTED__'

  return {
    imapHost,
    imapPort,
    imapEncryption,
    username,
    password: '',
    hasStoredPassword,
    mailbox,
    pollInterval,
    smtpHost,
    smtpPort,
    smtpEncryption,
    smtpUsername,
    smtpPassword: '',
    hasStoredSmtpPassword,
    fromName,
    fromEmail,
    replyToEmail,
    returnPath,
  }
}

const EmailImapForm: React.FC<EmailImapFormProps> = ({
  stationId,
  provider,
  initializing = false,
  onSaved,
  onNotify,
}) => {
  const [form, setForm] = React.useState<EmailFormState>(() => buildStateFromProvider(provider))
  const [initial, setInitial] = React.useState<EmailFormState>(() => buildStateFromProvider(provider))
  const [saving, setSaving] = React.useState(false)
  const [testingImap, setTestingImap] = React.useState(false)
  const [testingSmtp, setTestingSmtp] = React.useState(false)
  const [errors, setErrors] = React.useState<FormErrors>({})

  const status = provider?.status ?? 'not_configured'
  const providerError = typeof provider?.error === 'string' && provider.error !== '' ? provider.error : null

  React.useEffect(() => {
    const next = buildStateFromProvider(provider)
    setForm(next)
    setInitial(next)
    setErrors({})
  }, [provider])

  const setField = <K extends keyof EmailFormState>(field: K, value: EmailFormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const nextErrors = { ...prev }
        delete nextErrors[field]
        return nextErrors
      })
    }
  }

  const imapConfigured = React.useMemo(() => {
    return (
      initial.imapHost.trim() !== '' &&
      initial.username.trim() !== '' &&
      (initial.hasStoredPassword || initial.password.trim() !== '')
    )
  }, [initial])

  const smtpConfigured = React.useMemo(() => {
    return (
      initial.smtpHost.trim() !== '' &&
      initial.smtpUsername.trim() !== '' &&
      initial.fromEmail.trim() !== '' &&
      (initial.hasStoredSmtpPassword || initial.smtpPassword.trim() !== '')
    )
  }, [initial])

  const sanitizedSmtpPort = React.useMemo(() => Number.parseInt(form.smtpPort, 10) || 0, [form.smtpPort])

  const smtpDomainWarning = React.useMemo(() => {
    const fromDomain = form.fromEmail.includes('@') ? form.fromEmail.split('@').pop()?.toLowerCase() ?? '' : ''
    const usernameDomain = form.smtpUsername.includes('@')
      ? form.smtpUsername.split('@').pop()?.toLowerCase() ?? ''
      : ''

    if (!fromDomain || !usernameDomain) {
      return null
    }

    if (fromDomain !== usernameDomain) {
      return 'The From email domain should match the domain authenticated with your SMTP provider.'
    }

    return null
  }, [form.fromEmail, form.smtpUsername])

  const smtpEncryptionWarnings = React.useMemo(() => {
    const warnings: string[] = []

    if (form.smtpEncryption === 'none' && (sanitizedSmtpPort === 25 || sanitizedSmtpPort === 587)) {
      warnings.push('Port 25/587 without encryption is discouraged. Enable TLS/SSL to protect listener data.')
    }

    if (form.smtpEncryption !== 'none' && sanitizedSmtpPort === 25) {
      warnings.push('Port 25 is usually used for unencrypted delivery. Consider 465 (SSL) or 587 (TLS).')
    }

    return warnings
  }, [form.smtpEncryption, sanitizedSmtpPort])

  const isDirty = React.useMemo(() => {
    return (
      form.imapHost !== initial.imapHost ||
      form.imapPort !== initial.imapPort ||
      form.imapEncryption !== initial.imapEncryption ||
      form.username !== initial.username ||
      form.mailbox !== initial.mailbox ||
      form.pollInterval !== initial.pollInterval ||
      form.password.trim() !== '' ||
      form.smtpHost !== initial.smtpHost ||
      form.smtpPort !== initial.smtpPort ||
      form.smtpEncryption !== initial.smtpEncryption ||
      form.smtpUsername !== initial.smtpUsername ||
      form.smtpPassword.trim() !== '' ||
      form.fromName !== initial.fromName ||
      form.fromEmail !== initial.fromEmail ||
      form.replyToEmail !== initial.replyToEmail ||
      form.returnPath !== initial.returnPath
    )
  }, [form, initial])

  const shouldValidateSmtp = React.useCallback(
    (force: boolean) => {
      const host = form.smtpHost.trim()
      const username = form.smtpUsername.trim()
      const password = form.smtpPassword.trim()
      const fromEmail = form.fromEmail.trim()
      const replyTo = form.replyToEmail.trim()
      const returnPath = form.returnPath.trim()
      const fromName = form.fromName.trim()

      if (force) {
        return true
      }

      if (host !== '' || username !== '' || password !== '' || fromEmail !== '' || replyTo !== '' || returnPath !== '' || fromName !== '') {
        return true
      }

      return false
    },
    [form.fromEmail, form.fromName, form.replyToEmail, form.returnPath, form.smtpHost, form.smtpPassword, form.smtpUsername],
  )

  const validate = React.useCallback(
    (scope: 'all' | 'imap' | 'smtp' = 'all') => {
      const imapErrors: FormErrors = {}
      const smtpErrors: FormErrors = {}

      if (scope === 'all' || scope === 'imap') {
        if (form.imapHost.trim() === '') {
          imapErrors.imapHost = 'IMAP host is required.'
        }

        const portValue = Number.parseInt(form.imapPort, 10)
        if (Number.isNaN(portValue) || portValue <= 0) {
          imapErrors.imapPort = 'Enter a valid port number.'
        }

        if (!['ssl', 'tls', 'none'].includes(form.imapEncryption)) {
          imapErrors.imapEncryption = 'Select a valid encryption option.'
        }

        if (form.username.trim() === '') {
          imapErrors.username = 'Username is required.'
        }

        if (form.password.trim() === '' && !form.hasStoredPassword) {
          imapErrors.password = 'Password is required.'
        }

        if (form.pollInterval.trim() === '') {
          imapErrors.pollInterval = 'Poll interval is required.'
        } else {
          const interval = Number.parseInt(form.pollInterval, 10)
          if (Number.isNaN(interval) || interval < 1 || interval > 30) {
            imapErrors.pollInterval = 'Poll interval must be between 1 and 30 minutes.'
          }
        }
      }

      if (scope === 'all' || scope === 'smtp') {
        const host = form.smtpHost.trim()
        const username = form.smtpUsername.trim()
        const password = form.smtpPassword.trim()
        const fromName = form.fromName.trim()
        const fromEmail = form.fromEmail.trim()
        const replyTo = form.replyToEmail.trim()
        const returnPath = form.returnPath.trim()
        const requireSmtp = shouldValidateSmtp(scope === 'smtp')

        if (requireSmtp) {
          if (host === '') {
            smtpErrors.smtpHost = 'SMTP host is required.'
          }

          const portValue = Number.parseInt(form.smtpPort, 10)
          if (Number.isNaN(portValue) || portValue <= 0) {
            smtpErrors.smtpPort = 'Enter a valid port number.'
          }

          if (!['ssl', 'tls', 'none'].includes(form.smtpEncryption)) {
            smtpErrors.smtpEncryption = 'Select a valid encryption option.'
          }

          if (host !== '') {
            if (username === '') {
              smtpErrors.smtpUsername = 'SMTP username is required.'
            }

            if (password === '' && !form.hasStoredSmtpPassword) {
              smtpErrors.smtpPassword = 'SMTP password is required.'
            }

            if (fromEmail === '' || !emailPattern.test(fromEmail)) {
              smtpErrors.fromEmail = 'Enter a valid From email address.'
            }

            if (replyTo !== '' && !emailPattern.test(replyTo)) {
              smtpErrors.replyToEmail = 'Enter a valid Reply-To email address.'
            }

            if (returnPath !== '' && !emailPattern.test(returnPath)) {
              smtpErrors.returnPath = 'Enter a valid Return-Path email address.'
            }
          } else if (requireSmtp) {
            if (username !== '' || password !== '' || fromEmail !== '' || replyTo !== '' || returnPath !== '' || fromName !== '') {
              smtpErrors.smtpHost = 'Enter an SMTP host or clear the outbound fields to disable sending.'
            }
          }
        }
      }

      if (scope === 'all') {
        const mergedErrors = { ...imapErrors, ...smtpErrors }
        setErrors(mergedErrors)
        return Object.keys(mergedErrors).length === 0
      }

      if (scope === 'imap') {
        setErrors((prev) => {
          const next = { ...prev }
          IMAP_ERROR_FIELDS.forEach((field) => {
            if (imapErrors[field]) {
              next[field] = imapErrors[field]
            } else {
              delete next[field]
            }
          })
          return next
        })
        return Object.keys(imapErrors).length === 0
      }

      setErrors((prev) => {
        const next = { ...prev }
        SMTP_ERROR_FIELDS.forEach((field) => {
          if (smtpErrors[field]) {
            next[field] = smtpErrors[field]
          } else {
            delete next[field]
          }
        })
        return next
      })
      return Object.keys(smtpErrors).length === 0
    },
    [form, shouldValidateSmtp],
  )

  const buildSettingsPayload = React.useCallback((): Record<string, unknown> => {
    const trimmedPassword = form.password.trim()
    const trimmedSmtpPassword = form.smtpPassword.trim()
    const host = form.smtpHost.trim()

    const passwordPayload =
      trimmedPassword === ''
        ? initial.hasStoredPassword
          ? '__UNCHANGED__'
          : ''
        : trimmedPassword

    let smtpPasswordPayload: string
    if (host === '') {
      smtpPasswordPayload = ''
    } else if (trimmedSmtpPassword === '') {
      smtpPasswordPayload = initial.hasStoredSmtpPassword ? '__UNCHANGED__' : ''
    } else {
      smtpPasswordPayload = trimmedSmtpPassword
    }

    const pollIntervalNumber = Math.max(1, Math.min(30, Number.parseInt(form.pollInterval, 10) || 2))
    const smtpPortNumber = Number.parseInt(form.smtpPort, 10) || 0

    return {
      imap_host: form.imapHost.trim(),
      imap_port: Number.parseInt(form.imapPort, 10) || 0,
      imap_encryption: form.imapEncryption,
      username: form.username.trim(),
      password: passwordPayload,
      mailbox: form.mailbox.trim() === '' ? 'INBOX' : form.mailbox.trim(),
      poll_interval_minutes: pollIntervalNumber,
      smtp_host: host,
      smtp_port: smtpPortNumber,
      smtp_encryption: form.smtpEncryption,
      smtp_username: host === '' ? '' : form.smtpUsername.trim(),
      smtp_password: smtpPasswordPayload,
      from_name: host === '' ? '' : form.fromName.trim(),
      from_email: host === '' ? '' : form.fromEmail.trim(),
      reply_to_email: host === '' ? '' : form.replyToEmail.trim(),
      return_path: host === '' ? '' : form.returnPath.trim(),
    }
  }, [form, initial.hasStoredPassword, initial.hasStoredSmtpPassword])

  const applySuccessfulSave = React.useCallback(
    (payload: Record<string, unknown>) => {
      const trimmedImapHost = typeof payload.imap_host === 'string' ? payload.imap_host : ''
      const imapPort = typeof payload.imap_port === 'number' ? payload.imap_port : Number.parseInt(String(payload.imap_port ?? ''), 10)
      const imapEncryption = typeof payload.imap_encryption === 'string' ? payload.imap_encryption : form.imapEncryption
      const username = typeof payload.username === 'string' ? payload.username : ''
      const mailbox = typeof payload.mailbox === 'string' && payload.mailbox !== '' ? payload.mailbox : 'INBOX'
      const pollInterval = typeof payload.poll_interval_minutes === 'number'
        ? payload.poll_interval_minutes
        : Number.parseInt(String(payload.poll_interval_minutes ?? ''), 10) || 2
      const smtpHost = typeof payload.smtp_host === 'string' ? payload.smtp_host : ''
      const smtpPort = typeof payload.smtp_port === 'number'
        ? payload.smtp_port
        : Number.parseInt(String(payload.smtp_port ?? ''), 10) || (smtpHost === '' ? 587 : 0)
      const smtpEncryption = typeof payload.smtp_encryption === 'string' ? payload.smtp_encryption : form.smtpEncryption
      const smtpUsername = typeof payload.smtp_username === 'string' ? payload.smtp_username : ''
      const fromName = typeof payload.from_name === 'string' ? payload.from_name : ''
      const fromEmail = typeof payload.from_email === 'string' ? payload.from_email : ''
      const replyTo = typeof payload.reply_to_email === 'string' ? payload.reply_to_email : ''
      const returnPath = typeof payload.return_path === 'string' ? payload.return_path : ''

      const trimmedPassword = form.password.trim()
      const trimmedSmtpPassword = form.smtpPassword.trim()

      const savedHasStoredPassword =
        trimmedPassword !== ''
          ? true
          : payload.password === '__UNCHANGED__'
            ? initial.hasStoredPassword
            : String(payload.password ?? '') !== ''

      const savedHasStoredSmtpPassword = (() => {
        if (smtpHost === '') {
          return false
        }
        if (trimmedSmtpPassword !== '') {
          return true
        }
        if (payload.smtp_password === '__UNCHANGED__') {
          return initial.hasStoredSmtpPassword
        }
        return String(payload.smtp_password ?? '') !== ''
      })()

      const nextState: EmailFormState = {
        imapHost: trimmedImapHost,
        imapPort: String(imapPort > 0 ? imapPort : 993),
        imapEncryption: (['ssl', 'tls', 'none'] as const).includes(imapEncryption as 'ssl' | 'tls' | 'none')
          ? (imapEncryption as EmailFormState['imapEncryption'])
          : form.imapEncryption,
        username,
        password: '',
        hasStoredPassword: savedHasStoredPassword,
        mailbox,
        pollInterval: String(Math.max(1, Math.min(30, pollInterval))),
        smtpHost,
        smtpPort: smtpHost === '' ? '587' : String(smtpPort > 0 ? smtpPort : 587),
        smtpEncryption: (['ssl', 'tls', 'none'] as const).includes(smtpEncryption as 'ssl' | 'tls' | 'none')
          ? (smtpEncryption as EmailFormState['smtpEncryption'])
          : form.smtpEncryption,
        smtpUsername,
        smtpPassword: '',
        hasStoredSmtpPassword: savedHasStoredSmtpPassword,
        fromName,
        fromEmail,
        replyToEmail: replyTo,
        returnPath,
      }

      setForm(nextState)
      setInitial(nextState)
      setErrors({})
    },
    [form.imapEncryption, form.smtpEncryption, form.smtpPassword, form.password, initial.hasStoredPassword, initial.hasStoredSmtpPassword],
  )

  const handleSave = async () => {
    if (saving) {
      return
    }

    if (!validate('all')) {
      return
    }

    setSaving(true)

    const payload = buildSettingsPayload()

    try {
      const response = await saveIntegration('email', stationId, payload)

      if (!response.ok) {
        throw new Error('Save request failed.')
      }

      applySuccessfulSave(payload)

      await Promise.resolve(onSaved())

      onNotify('success', 'Email settings saved.')
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Unable to save Email settings.')
            : typeof error === 'object' && error !== null && 'code' in error
              ? String((error as { code?: unknown }).code ?? 'Unable to save Email settings.')
              : 'Unable to save Email settings.'
      onNotify('error', message)
    } finally {
      setSaving(false)
    }
  }

  const handleTestImap = async () => {
    if (testingImap) {
      return
    }

    setTestingImap(true)

    try {
      const result = await testIntegration('email', stationId)

      if (result.ok) {
        const message = result.details ?? 'IMAP connection successful.'
        onNotify('success', message)
      } else {
        const message = result.error ?? 'IMAP test failed.'
        onNotify('error', message)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'IMAP test failed.'
      onNotify('error', message)
    } finally {
      setTestingImap(false)
    }
  }

  const handleTestSmtp = async () => {
    if (testingSmtp) {
      return
    }

    if (!validate('smtp')) {
      return
    }

    setTestingSmtp(true)

    const payload = buildSettingsPayload()

    try {
      const result: TestIntegrationResponse = await testEmailSmtp(stationId, payload)

      if (result.ok) {
        const message = result.details ?? 'SMTP OK.'
        onNotify('success', message)
      } else {
        const message = result.error ?? 'SMTP test failed.'
        onNotify('error', message)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'SMTP test failed.'
      onNotify('error', message)
    } finally {
      setTestingSmtp(false)
    }
  }

  const canTestSmtp = React.useMemo(() => {
    const host = form.smtpHost.trim()
    const username = form.smtpUsername.trim()
    const fromEmail = form.fromEmail.trim()
    const hasPassword = form.smtpPassword.trim() !== '' || form.hasStoredSmtpPassword
    return (
      host !== '' &&
      username !== '' &&
      fromEmail !== '' &&
      emailPattern.test(fromEmail) &&
      hasPassword &&
      sanitizedSmtpPort > 0
    )
  }, [form.fromEmail, form.hasStoredSmtpPassword, form.smtpHost, form.smtpPassword, form.smtpUsername, sanitizedSmtpPort])

  if (initializing && !provider) {
    return (
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Skeleton variant="text" width="45%" />
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Skeleton variant="text" width="55%" />
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          </Stack>
        </Paper>
      </Stack>
    )
  }

  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Stack spacing={0.5}>
              <Typography variant="h6">Email (IMAP)</Typography>
              <Typography variant="body2" color="text.secondary">
                Connect an IMAP inbox to receive listener emails directly inside the unified inbox.
              </Typography>
            </Stack>
            <Chip
              label={imapConfigured ? 'Connected' : 'Not configured'}
              color={imapConfigured ? 'success' : 'default'}
              variant={imapConfigured ? 'filled' : 'outlined'}
              size="small"
            />
          </Stack>

          <Stack spacing={2}>
            {providerError && status === 'corrupt' ? (
              <Alert severity="error">Configuration could not be decrypted: {providerError}</Alert>
            ) : null}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="IMAP Host"
                value={form.imapHost}
                onChange={(event) => setField('imapHost', event.target.value)}
                error={Boolean(errors.imapHost)}
                helperText={errors.imapHost}
                fullWidth
                autoComplete="off"
              />
              <TextField
                label="Port"
                value={form.imapPort}
                onChange={(event) => setField('imapPort', event.target.value)}
                error={Boolean(errors.imapPort)}
                helperText={errors.imapPort}
                fullWidth
                type="number"
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Encryption"
                select
                value={form.imapEncryption}
                onChange={(event) =>
                  setField('imapEncryption', event.target.value as EmailFormState['imapEncryption'])
                }
                error={Boolean(errors.imapEncryption)}
                helperText={errors.imapEncryption}
                fullWidth
              >
                {encryptionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Username"
                value={form.username}
                onChange={(event) => setField('username', event.target.value)}
                error={Boolean(errors.username)}
                helperText={errors.username}
                fullWidth
                autoComplete="off"
              />
              <TextField
                label="Password"
                type="password"
                value={form.password}
                onChange={(event) => setField('password', event.target.value)}
                error={Boolean(errors.password)}
                helperText={
                  errors.password ??
                  (form.hasStoredPassword ? 'Leave blank to keep the existing password' : undefined)
                }
                fullWidth
                autoComplete="new-password"
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Mailbox"
                value={form.mailbox}
                onChange={(event) => setField('mailbox', event.target.value)}
                error={Boolean(errors.mailbox)}
                helperText={errors.mailbox}
                fullWidth
                autoComplete="off"
              />
              <TextField
                label="Poll Interval (minutes)"
                value={form.pollInterval}
                onChange={(event) => setField('pollInterval', event.target.value)}
                error={Boolean(errors.pollInterval)}
                helperText={errors.pollInterval ?? 'Between 1 and 30 minutes'}
                fullWidth
                type="number"
                inputProps={{ min: 1, max: 30 }}
              />
            </Stack>

            {form.hasStoredPassword && form.password.trim() === '' ? (
              <Alert severity="info">Stored IMAP password is hidden. Enter a new password to replace it.</Alert>
            ) : null}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleTestImap} disabled={testingImap || !imapConfigured}>
                {testingImap ? <CircularProgress size={16} /> : 'Test Connection'}
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving || !isDirty}>
                {saving ? <CircularProgress size={16} /> : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Stack spacing={0.5}>
              <Typography variant="h6">Outbound (SMTP)</Typography>
              <Typography variant="body2" color="text.secondary">
                Configure outbound delivery so presenters can reply to listeners by email directly from RPA.
              </Typography>
            </Stack>
            <Chip
              label={smtpConfigured ? 'Configured' : 'Not configured'}
              color={smtpConfigured ? 'success' : 'default'}
              variant={smtpConfigured ? 'filled' : 'outlined'}
              size="small"
            />
          </Stack>

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="SMTP Host"
                value={form.smtpHost}
                onChange={(event) => setField('smtpHost', event.target.value)}
                error={Boolean(errors.smtpHost)}
                helperText={errors.smtpHost}
                fullWidth
                autoComplete="off"
              />
              <TextField
                label="Port"
                value={form.smtpPort}
                onChange={(event) => setField('smtpPort', event.target.value)}
                error={Boolean(errors.smtpPort)}
                helperText={errors.smtpPort}
                fullWidth
                type="number"
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Encryption"
                select
                value={form.smtpEncryption}
                onChange={(event) =>
                  setField('smtpEncryption', event.target.value as EmailFormState['smtpEncryption'])
                }
                error={Boolean(errors.smtpEncryption)}
                helperText={errors.smtpEncryption}
                fullWidth
              >
                {encryptionOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="SMTP Username"
                value={form.smtpUsername}
                onChange={(event) => setField('smtpUsername', event.target.value)}
                error={Boolean(errors.smtpUsername)}
                helperText={errors.smtpUsername}
                fullWidth
                autoComplete="off"
              />
              <TextField
                label="SMTP Password"
                type="password"
                value={form.smtpPassword}
                onChange={(event) => setField('smtpPassword', event.target.value)}
                error={Boolean(errors.smtpPassword)}
                helperText={
                  errors.smtpPassword ??
                  (form.hasStoredSmtpPassword ? 'Leave blank to keep the existing password' : undefined)
                }
                fullWidth
                autoComplete="new-password"
              />
            </Stack>

            {form.hasStoredSmtpPassword && form.smtpPassword.trim() === '' ? (
              <Alert severity="info">Stored SMTP password is hidden. Enter a new password to replace it.</Alert>
            ) : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="From Name"
                value={form.fromName}
                onChange={(event) => setField('fromName', event.target.value)}
                fullWidth
                autoComplete="off"
              />
              <TextField
                label="From Email"
                value={form.fromEmail}
                onChange={(event) => setField('fromEmail', event.target.value)}
                error={Boolean(errors.fromEmail)}
                helperText={errors.fromEmail}
                fullWidth
                autoComplete="email"
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Reply-To Email (optional)"
                value={form.replyToEmail}
                onChange={(event) => setField('replyToEmail', event.target.value)}
                error={Boolean(errors.replyToEmail)}
                helperText={errors.replyToEmail}
                fullWidth
                autoComplete="email"
              />
              <TextField
                label="Return-Path (bounce, optional)"
                value={form.returnPath}
                onChange={(event) => setField('returnPath', event.target.value)}
                error={Boolean(errors.returnPath)}
                helperText={errors.returnPath}
                fullWidth
                autoComplete="email"
              />
            </Stack>

            {smtpDomainWarning ? <Alert severity="warning">{smtpDomainWarning}</Alert> : null}
            {smtpEncryptionWarnings.map((warning) => (
              <Alert key={warning} severity="warning">
                {warning}
              </Alert>
            ))}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleTestSmtp} disabled={testingSmtp || !canTestSmtp}>
                {testingSmtp ? <CircularProgress size={16} /> : 'Test SMTP'}
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving || !isDirty}>
                {saving ? <CircularProgress size={16} /> : 'Save'}
              </Button>
            </Stack>

            <Divider flexItem sx={{ my: 1 }} />

            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Improve deliverability</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense sx={{ pl: 1 }}>
                  <ListItem disableGutters>
                    <ListItemText primary="Use a domain you control" secondary="Send from an address like studio@yourstation.com rather than a consumer mailbox." />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemText primary="SPF" secondary="Add or extend your DNS TXT record to include your SMTP provider (for example: v=spf1 include:_spf.yourprovider.com ~all)." />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemText primary="DKIM" secondary="Enable DKIM signing with your mail provider and publish the selector key in DNS." />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemText primary="DMARC" secondary="Add a DMARC record such as v=DMARC1; p=quarantine; rua=mailto:postmaster@yourstation.com and adjust the policy once you are confident." />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemText primary="Match domains" secondary="The From address should use the same domain that your SMTP service authenticated." />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemText primary="Return-Path" secondary="If your provider uses a specific bounce mailbox, set it in Return-Path so bounces are routed correctly." />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemText primary="Provider docs" secondary="Check your SMTP provider’s deliverability guides for DNS and authentication steps tailored to their service." />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  )
}

export default EmailImapForm
